import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Menu, X, ChevronDown } from "lucide-react";
import Logo from "./Logo";
import { api } from "../lib/api";
import AnnouncementBar from "./AnnouncementBar";

const SOBRE_SUB = [
  { label: "Sobre Orion", to: "/sobre" },
  { label: "Corporativo", to: "/agencia-de-viagens-corporativa" },
  { label: "Educacional", to: "/educacional" },
  { label: "Formaturas", to: "/formaturas" },
  { label: "Intercâmbio", to: "/intercambio" },
  { label: "Cruzeiros", to: "/cruzeiros" },
  { label: "Excursões", to: "https://viagensorion.com", external: true },
];

// Extra virtual entries injected into DB-driven category dropdowns
// Key: parent category slug, Value: array of custom links merged alphabetically
// with the DB categories. Set `isNew: true` to render a subtle "(novidade)" tag.
const EXTRA_MENU_ENTRIES = {
  "estilos-de-viagem": [
    { label: "Internacional 3 em 1", to: "/roteiros/internacional-3-em-1", isNew: true },
    { label: "Viagens para Mulheres", to: "/viagens-para-mulheres", isNew: true },
  ],
};

export default function Header() {
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openParent, setOpenParent] = useState(null); // "sobre" | category slug
  const [mobileParent, setMobileParent] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const nav = useNavigate();
  const closeTimer = useRef(null);

  useEffect(() => {
    api.get("/categories", { params: { only_active: true } })
      .then((r) => setCats(r.data))
      .catch(() => {});
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const parents = cats.filter((c) => !c.parent_slug);
  const childrenOf = (slug) => cats.filter((c) => c.parent_slug === slug);

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) {
      nav(`/busca?q=${encodeURIComponent(q.trim())}`);
      setSearchOpen(false); setMenuOpen(false);
    }
  };

  const enter = (slug) => { if (closeTimer.current) clearTimeout(closeTimer.current); setOpenParent(slug); };
  const leave = () => { closeTimer.current = setTimeout(() => setOpenParent(null), 120); };

  // Header always has a solid background (cream), so it's readable over any section.
  const headerCls = scrolled
    ? "bg-white/95 shadow-[0_10px_40px_rgb(44,17,57,0.08)] border-b border-[#EFEAE0]"
    : "bg-orion-cream border-b border-[#EFEAE0]";

  return (
    <div className="sticky top-0 z-40" data-testid="site-header-wrapper">
      <AnnouncementBar />
      <header className={`backdrop-blur-md transition-all duration-300 ${headerCls}`} data-testid="site-header">
      <div className="container-orion py-3 md:py-4 flex items-center gap-4">
        {/* Logo à esquerda (posição original) */}
        <div className="flex items-center gap-3 shrink-0">
          <Logo />
        </div>

        {/* Menu de categorias — bloco compacto centralizado. Home no início, gap enxuto e letter-spacing leve. */}
        <nav className="hidden lg:flex mx-auto items-center gap-x-3 xl:gap-x-4 text-[11px] xl:text-[12px] uppercase tracking-[0.10em] xl:tracking-[0.12em] font-semibold text-[#2C1139]/85">
          <NavLink to="/" end className="nav-link inline-flex items-center gap-1 hover:text-orion-orange transition-colors whitespace-nowrap" data-testid="nav-home">
            Home
          </NavLink>
          {parents.map((p) => {
            const subs = childrenOf(p.slug);
            const extras = EXTRA_MENU_ENTRIES[p.slug] || [];
            // Merge subs + extras into one alphabetically-sorted list so the
            // new "Internacional 3 em 1" and "Viagens para Mulheres" sit next to
            // the other categories in dictionary order (Portuguese-aware).
            const items = [
              ...subs.map((s) => ({ key: `sub-${s.id}`, label: s.name, to: `/categoria/${s.slug}`, testId: `sub-${s.slug}`, isNew: false })),
              ...extras.map((x) => ({ key: `extra-${x.to}`, label: x.label, to: x.to, testId: `sub-extra-${x.to.replace(/\W+/g, "-")}`, isNew: !!x.isNew })),
            ].sort((a, b) => a.label.localeCompare(b.label, "pt-BR", { sensitivity: "base" }));
            const hasSubs = items.length > 0;
            return (
              <div key={p.id} className="relative" onMouseEnter={() => hasSubs && enter(p.slug)} onMouseLeave={leave}>
                <NavLink to={`/categoria/${p.slug}`} className="nav-link inline-flex items-center gap-1 hover:text-orion-orange transition-colors whitespace-nowrap" data-testid={`nav-parent-${p.slug}`}>
                  {p.name}
                  {hasSubs && <ChevronDown size={12} className="opacity-60" />}
                </NavLink>
                {hasSubs && openParent === p.slug && (
                  <div className="absolute left-1/2 -translate-x-1/2 top-full mt-3 bg-white shadow-[0_18px_50px_rgb(44,17,57,0.14)] border-t-2 border-orion-orange py-6 px-8 min-w-[520px] grid grid-cols-2 gap-x-10 gap-y-2 z-50" onMouseEnter={() => enter(p.slug)} onMouseLeave={leave} data-testid={`dropdown-${p.slug}`}>
                    {items.map((it) => (
                      <Link
                        key={it.key}
                        to={it.to}
                        className="text-[13px] tracking-normal normal-case font-normal text-[#2C1139] hover:text-orion-orange py-1 whitespace-nowrap"
                        onClick={() => setOpenParent(null)}
                        data-testid={it.testId}
                      >
                        {it.label}
                        {it.isNew && (
                          <span className="ml-1.5 text-[10px] italic text-orion-orange/85 font-normal lowercase tracking-normal">
                            (novidade)
                          </span>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {/* Serviços dropdown — parent link goes to /sobre */}
          <div className="relative" onMouseEnter={() => enter("__sobre__")} onMouseLeave={leave}>
            <NavLink to="/sobre" className="nav-link inline-flex items-center gap-1 hover:text-orion-orange transition-colors whitespace-nowrap" data-testid="nav-sobre">
              Serviços <ChevronDown size={12} className="opacity-60" />
            </NavLink>
            {openParent === "__sobre__" && (
              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-3 bg-white shadow-[0_18px_50px_rgb(44,17,57,0.14)] border-t-2 border-orion-orange py-5 px-6 min-w-[240px] z-50 flex flex-col gap-1" onMouseEnter={() => enter("__sobre__")} onMouseLeave={leave} data-testid="dropdown-sobre">
                {SOBRE_SUB.map((s) => (
                  s.external ? (
                    <a
                      key={s.to}
                      href={s.to}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[13px] tracking-normal normal-case font-normal text-[#2C1139] hover:text-orion-orange py-1 inline-flex items-center gap-1"
                      onClick={() => setOpenParent(null)}
                      data-testid={`sub-sobre-${s.label.toLowerCase().replace(/[^a-z]+/g,"-")}`}
                    >
                      {s.label} <span className="text-[10px] opacity-60">↗</span>
                    </a>
                  ) : (
                    <Link
                      key={s.to}
                      to={s.to}
                      className="text-[13px] tracking-normal normal-case font-normal text-[#2C1139] hover:text-orion-orange py-1"
                      onClick={() => setOpenParent(null)}
                      data-testid={`sub-sobre-${s.label.toLowerCase().replace(/[^a-z]+/g,"-")}`}
                    >
                      {s.label}
                    </Link>
                  )
                ))}
              </div>
            )}
          </div>
          {/* Risco de Golpe — plain link to /cuidados (dropdown removed per user preference) */}
          <NavLink to="/cuidados" className="nav-link inline-flex items-center gap-1 hover:text-orion-orange transition-colors whitespace-nowrap" data-testid="nav-risco-golpe">
            Risco de Golpe
          </NavLink>
        </nav>

        {/* Ícones à direita — apenas lupa + hamburger mobile */}
        <div className="flex items-center gap-2 shrink-0 ml-auto lg:ml-0">
          <button onClick={() => setSearchOpen((v) => !v)} className="p-2 text-[#2C1139] hover:text-orion-orange transition-colors" data-testid="header-search-toggle" aria-label="Buscar">
            <Search size={20} />
          </button>
          <button onClick={() => setMenuOpen((v) => !v)} className="lg:hidden p-2 text-[#2C1139] hover:text-orion-orange transition-colors" data-testid="mobile-menu-toggle" aria-label="Menu">
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-[#EFEAE0] bg-white" data-testid="search-overlay">
          <div className="container-orion py-4">
            <form onSubmit={submit} className="flex items-center gap-3 border-b-2 border-orion-purple pb-2">
              <Search size={20} className="text-orion-purple shrink-0" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar destino, roteiro ou palavra-chave…" className="flex-1 bg-transparent outline-none text-lg py-1" data-testid="header-search-input" />
              <button type="submit" className="bg-orion-orange text-white px-4 py-2 text-xs uppercase tracking-widest hover:bg-[#E04828] transition-colors" data-testid="header-search-submit">Buscar</button>
              <button type="button" onClick={() => setSearchOpen(false)} className="text-[#8A8191] hover:text-orion-purple p-1" aria-label="Fechar"><X size={18} /></button>
            </form>
          </div>
        </div>
      )}

      {menuOpen && (
        <div className="lg:hidden border-t border-[#EFEAE0] bg-white max-h-[70vh] overflow-y-auto">
          <div className="container-orion py-3">
            {parents.map((p) => {
              const subs = childrenOf(p.slug);
              const extras = EXTRA_MENU_ENTRIES[p.slug] || [];
              const isOpen = mobileParent === p.slug;
              return (
                <div key={p.id} className="border-b border-[#EFEAE0] last:border-0">
                  <button onClick={() => setMobileParent(isOpen ? null : p.slug)} className="w-full flex items-center justify-between py-3 text-xs font-semibold text-orion-purple uppercase tracking-widest" data-testid={`mobile-parent-${p.slug}`}>
                    {p.name}
                    <ChevronDown size={16} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isOpen && (
                    <div className="pb-3 pl-2 grid grid-cols-2 gap-1">
                      <Link to={`/categoria/${p.slug}`} onClick={() => setMenuOpen(false)} className="text-xs uppercase tracking-widest text-orion-orange col-span-2 py-1">Ver todos em {p.name}</Link>
                      {subs.map((s) => (
                        <Link key={s.id} to={`/categoria/${s.slug}`} onClick={() => setMenuOpen(false)} className="text-sm text-[#2C1139] hover:text-orion-orange py-1.5" data-testid={`mobile-sub-${s.slug}`}>{s.name}</Link>
                      ))}
                      {extras.map((x) => (
                        <Link key={x.to} to={x.to} onClick={() => setMenuOpen(false)} className="text-sm text-[#2C1139] hover:text-orion-orange py-1.5">{x.label}</Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <Link to="/sobre" onClick={() => setMenuOpen(false)} data-testid="mobile-nav-sobre" className="block py-3 text-xs text-orion-purple font-semibold uppercase tracking-widest border-b border-[#EFEAE0]">Serviços</Link>
            <div className="pl-2 pb-2 grid gap-1 border-b border-[#EFEAE0]">
              {SOBRE_SUB.map((s) => (
                s.external ? (
                  <a
                    key={s.to}
                    href={s.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMenuOpen(false)}
                    className="text-sm text-[#2C1139] hover:text-orion-orange py-1.5 inline-flex items-center gap-1"
                    data-testid={`mobile-sub-${s.label.toLowerCase().replace(/[^a-z]+/g,"-")}`}
                  >
                    · {s.label} <span className="text-[10px] opacity-60">↗</span>
                  </a>
                ) : (
                  <Link
                    key={s.to}
                    to={s.to}
                    onClick={() => setMenuOpen(false)}
                    className="text-sm text-[#2C1139] hover:text-orion-orange py-1.5"
                    data-testid={`mobile-sub-${s.label.toLowerCase().replace(/[^a-z]+/g,"-")}`}
                  >
                    · {s.label}
                  </Link>
                )
              ))}
            </div>
            <Link to="/cuidados" onClick={() => setMenuOpen(false)} className="block py-3 text-xs text-orion-orange font-semibold uppercase tracking-widest" data-testid="mobile-nav-risco">Risco de Golpe</Link>
          </div>
        </div>
      )}
    </header>
    </div>
  );
}
